import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import ScrollToTop from './ScrollToTop';
import { initializeCurrency } from './utils/currency';
import HomePage from './HomePage';
import FlightResultsPage from './FlightResultsPage';
import FlightDetailsPage from './FlightDetailsPage';
import PassengerInfoPage from './PassengerInfoPage';
import PaymentPage from './PaymentPage';
import BookingSuccessPage from './BookingSuccessPage';
import LoginPage from './LoginPage';
import SignUpPage from './SignUpPage';
import DashboardPage from './DashboardPage';
import TripDetailsPage from './TripDetailsPage';
import ProfileSettingsPage from './ProfileSettingsPage';
import PaymentMethodsPage from './PaymentMethodsPage';
import SupportPage from './SupportPage';
import BookFlightPage from './BookFlightPage';
import DestinationsPage from './DestinationsPage';
import ContactPage from './ContactPage';
import FAQPage from './FAQPage';
import AboutPage from './AboutPage';
import TermsAndConditionsPage from './TermsAndConditionsPage';
import PrivateJetsPage from './PrivateJetsPage';
import HotelsPage from './HotelsPage';
import HotelDetailsPage from './HotelDetailsPage';
import HotelBookingPage from './HotelBookingPage';
import HotelBookingThankYouPage from './HotelBookingThankYouPage';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';
import Users from './Users';
import UserDetails from './UserDetails';
import Flights from './Flights';
import AddFlight from './AddFlight';
import Bookings from './Bookings';
import BookingDetails from './BookingDetails';
import Payments from './Payments';
import Destinations from './Destinations';
import AddDestination from './AddDestination';
import AdminRoutes from './Routes';
import AddRoute from './AddRoute';
import Promotions from './Promotions';
import AddPromotion from './AddPromotion';
import SupportTickets from './SupportTickets';
import TicketDetails from './TicketDetails';
import Reports from './Reports';
import Settings from './Settings';
import AdminContacts from './AdminContacts';
import NewsletterSubscriptions from './NewsletterSubscriptions';
import PrivateJets from './PrivateJets';
import AddPrivateJet from './AddPrivateJet';
import FlightComparisonPage from './FlightComparisonPage';
import FlightSearchTestPage from './FlightSearchTestPage';
import DuffelTestPage from './DuffelTestPage';
import HotelSearchPage from './HotelSearchPage';
import LocalFlightsPage from './LocalFlightsPage';
import InternationalFlightsPage from './InternationalFlightsPage';
import WhatsAppButton from './WhatsAppButton';
import LiveChat from './LiveChat';
import AdminChat from './AdminChat';
import './App.css';

// Component to conditionally render chat buttons
const ConditionalChatButtons = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  if (isAdminRoute) {
    return null;
  }
  
  return (
    <>
      <LiveChat />
      <WhatsAppButton />
    </>
  );
};

function App() {
  // Initialize currency on app load - FORCE FRESH DETECTION
  useEffect(() => {
    const initCurrency = async () => {
      try {
        console.log('App: Initializing currency on app load with fresh detection...');
        // Force fresh detection on app load
        const currency = await initializeCurrency(true);
        console.log('App: Currency initialized:', {
          code: currency.code,
          symbol: currency.symbol,
          name: currency.name
        });
        // Force a re-render by dispatching a custom event
        window.dispatchEvent(new CustomEvent('currencyInitialized', { detail: currency }));
      } catch (error) {
        console.error('App: Error initializing currency:', error);
      }
    };
    initCurrency();
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <ConditionalChatButtons />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/book-flight" element={<BookFlightPage />} />
          <Route path="/local-flights" element={<LocalFlightsPage />} />
          <Route path="/international-flights" element={<InternationalFlightsPage />} />
          <Route path="/destinations" element={<DestinationsPage />} />
          <Route path="/results" element={<FlightResultsPage />} />
          <Route path="/compare-flights" element={<FlightComparisonPage />} />
          <Route path="/details" element={<FlightDetailsPage />} />
          <Route path="/passenger-info" element={<PassengerInfoPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/success" element={<BookingSuccessPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/trip/:id" element={<TripDetailsPage />} />
          <Route path="/profile" element={<ProfileSettingsPage />} />
          <Route path="/payment-methods" element={<PaymentMethodsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/private-jets" element={<PrivateJetsPage />} />
          <Route path="/hotels" element={<HotelsPage />} />
          <Route path="/hotel-details" element={<HotelDetailsPage />} />
          <Route path="/hotel-booking" element={<HotelBookingPage />} />
          <Route path="/hotel-booking-thank-you" element={<HotelBookingThankYouPage />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<Users />} />
          <Route path="/admin/users/:userId" element={<UserDetails />} />
          <Route path="/admin/flights" element={<Flights />} />
          <Route path="/admin/flights/add" element={<AddFlight />} />
          <Route path="/admin/bookings" element={<Bookings />} />
          <Route path="/admin/bookings/:bookingId" element={<BookingDetails />} />
          <Route path="/admin/payments" element={<Payments />} />
          <Route path="/admin/chat" element={<AdminChat />} />
          <Route path="/admin/contacts" element={<AdminContacts />} />
          <Route path="/admin/newsletter" element={<NewsletterSubscriptions />} />
          <Route path="/admin/private-jets" element={<PrivateJets />} />
          <Route path="/admin/private-jets/add" element={<AddPrivateJet />} />
          <Route path="/admin/destinations" element={<Destinations />} />
          <Route path="/admin/destinations/add" element={<AddDestination />} />
          <Route path="/admin/routes" element={<AdminRoutes />} />
          <Route path="/admin/routes/add" element={<AddRoute />} />
          <Route path="/admin/promotions" element={<Promotions />} />
          <Route path="/admin/promotions/add" element={<AddPromotion />} />
          <Route path="/admin/support-tickets" element={<SupportTickets />} />
          <Route path="/admin/support-tickets/:ticketId" element={<TicketDetails />} />
          <Route path="/admin/reports" element={<Reports />} />
          <Route path="/admin/settings" element={<Settings />} />
          {/* Flight search smoke test (Duffel); legacy /test-amadeus redirects here */}
          <Route path="/test-flight-search" element={<FlightSearchTestPage />} />
          <Route path="/test-amadeus" element={<Navigate to="/test-flight-search" replace />} />
          <Route path="/test-duffel" element={<DuffelTestPage />} />
          <Route path="/hotels" element={<HotelSearchPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
